import React from 'react';
import { Card } from '../../components/ui/card';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { centsToDollarString } from '../constants';

export const HEALTH_META = {
  under_budget: { label: 'Under Budget', cls: 'text-green-400 border-green-500/40 bg-green-500/5' },
  at_budget: { label: 'At Budget', cls: 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5' },
  over_budget: { label: 'Over Budget', cls: 'text-red-400 border-red-500/40 bg-red-500/5' },
  no_budget: { label: 'No Budget Set', cls: 'text-gray-400 border-gray-700 bg-gray-800/30' },
};

const money = (v) => (v == null ? null : centsToDollarString(v));

const Row = ({ label, value, hint, onClick, testId, accent }) => {
  const inner = (
    <>
      <div className="min-w-0">
        <div className="text-sm text-gray-300">{label}</div>
        {hint && <div className="text-[11px] text-gray-600 mt-0.5">{hint}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-sm font-semibold ${accent || 'text-white'}`}>{value ?? 'Not entered'}</span>
        {onClick && <ChevronRight size={14} className="text-gray-600" />}
      </div>
    </>
  );
  const cls = 'w-full flex items-center justify-between gap-3 px-4 py-3';
  return onClick ? (
    <button onClick={onClick} className={`${cls} text-left hover:bg-[#1A1A1A] transition-colors`} data-testid={testId}>
      {inner}
    </button>
  ) : (
    <div className={cls} data-testid={testId}>{inner}</div>
  );
};

// Shared simplified financial summary. `fin` uses the authoritative backend fields.
// `linkFor(key)` returns an onClick handler (or undefined) per row.
export const FinancialSummaryView = ({ fin, linkFor = () => undefined, titleNote }) => {
  const health = HEALTH_META[fin.budget_health] || HEALTH_META.no_budget;
  const profit = fin.potential_gross_profit_cents;
  const profitAccent = profit == null ? 'text-gray-400' : profit >= 0 ? 'text-green-400' : 'text-red-400';

  return (
    <Card className="bg-[#111111] border-gray-800 overflow-hidden" data-testid="financial-summary-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div>
          <h4 className="text-sm font-semibold text-white">Financial Summary</h4>
          {titleNote && <p className="text-[11px] text-gray-600 mt-0.5">{titleNote}</p>}
        </div>
        <span className={`text-xs border rounded-full px-2.5 py-1 ${health.cls}`} data-testid="budget-health-badge">
          {health.label}
        </span>
      </div>
      <div className="divide-y divide-gray-800/70">
        <Row label="Customer Budget" value={money(fin.customer_budget_cents)} onClick={linkFor('budget')} testId="fin-row-budget" />
        <Row label="Approved Change Orders" value={money(fin.approved_change_order_total_cents)}
          hint={fin.approved_change_order_count ? `${fin.approved_change_order_count} approved` : 'None approved yet'}
          onClick={linkFor('change_orders')} testId="fin-row-change-orders" />
        <Row label="Materials / Miscellaneous Costs" value={money(fin.materials_misc_cents)} onClick={linkFor('materials')} testId="fin-row-materials" />
        <Row label="Reimbursements (money back)" value={fin.reimbursements_cents ? `− ${money(fin.reimbursements_cents)}` : money(fin.reimbursements_cents ?? 0)}
          hint="Returns & refunds reduce costs" onClick={linkFor('reimbursements')} testId="fin-row-reimbursements"
          accent={fin.reimbursements_cents ? 'text-green-400' : undefined} />
        <Row label="Employee Costs" value={money(fin.employee_costs_cents)} onClick={linkFor('employees')} testId="fin-row-employees" />
        <Row label="Subcontractor Costs" value={money(fin.subcontractor_costs_cents)} onClick={linkFor('subs')} testId="fin-row-subs" />
        <Row label="Potential Gross Profit"
          value={profit == null ? 'Needs customer budget' : money(profit)}
          hint="(Budget + Change Orders) − all costs" accent={profitAccent} testId="fin-row-profit" />
        <Row label="Profit Percentage"
          value={fin.profit_pct_bps == null ? '—' : `${(fin.profit_pct_bps / 100).toFixed(1)}%`}
          hint="Profit ÷ (Budget + Change Orders)" accent={profitAccent} testId="fin-row-profit-pct" />
      </div>
      {(fin.warnings || []).length > 0 && (
        <div className="border-t border-gray-800 px-4 py-3 space-y-1.5" data-testid="fin-warnings">
          {fin.warnings.map((w) => (
            <p key={w.code} className="text-[11px] text-amber-400 flex items-start gap-1.5">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {w.message}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
};
